import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';
import * as _ from 'lodash';
import { InspectionPlugin, Inspection, InspectionPoint, InspectionFolder, InspectionSection } from '../models';
import { IndexedDbService } from './indexedDb.service';
import { AppConstants } from '../../utils/appConstants.class';
import { switchMap, map } from 'rxjs/operators';
import { Observable, of, zip } from 'rxjs';
import { ApplicationType } from '../../utils/enums';
import { BaseService } from './baseService';
import { DataService } from './data.service';

@Injectable({
  providedIn: 'root'
})
export class InspectionFactoryService extends BaseService {
  constructor(protected indexedDb: IndexedDbService, protected dataService: DataService) {
    super();
  }

  public updatePluginUrl(inspectionId: string, plugins: InspectionPlugin[]) {
    this.getInspection(inspectionId).subscribe((inspection) => {
      plugins.forEach((plugin) => {
        const pl = inspection.allPlugins.find((p) => p.id === plugin.id);
        if (pl) {
          pl.postServerUrl = plugin.postServerUrl;
        }
        const inspectionPlugin = inspection.plugins.find((p) => p.id === plugin.id);
        if (inspectionPlugin) {
          inspectionPlugin.postServerUrl = plugin.postServerUrl;
        }
      });
      this.updateInspection(inspection, false);
    });
  }

  public addInspection(inspection: Inspection): Observable<any> {
    return this.indexedDb.put(AppConstants.INDEXEDDB_TABLES.inspections, JSON.parse(JSON.stringify(inspection)), inspection.id);
  }

  public addServerPlugins(inspectionId: string, pointId: string, plugins: InspectionPlugin[]): Observable<any> {
    if (this.isManager) {
      return of(null);
    }
    return this.getInspection(inspectionId).pipe(
      switchMap((inspection) => {
        plugins.forEach((plugin) => {
          const existing = inspection.allPlugins.find((p) => p.id === plugin.id);
          if (existing) {
            _.merge(existing, plugin);
          } else {
            inspection.allPlugins.push(plugin);
          }
        });
        return this.indexedDb.put(AppConstants.INDEXEDDB_TABLES.inspections, JSON.parse(JSON.stringify(inspection)), inspection.id);
      })
    );
  }

  public addPlugins(inspectionId: string, pointId: string, pluginTypes: string[]): Observable<any> {
    return this.getInspection(inspectionId).pipe(
      switchMap((inspection) => {
        const obs = [];

        pluginTypes.forEach((t) => {
          obs.push(this._addPlugin(inspection, pointId, t));
        });

        if (pluginTypes.includes('pointparts')) {
          const partsPlugins = inspection.allPlugins.find((p) => p.pluginType === 'parts');
          if (!partsPlugins) {
            obs.push(this._addPlugin(inspection, null, 'parts'));
          }
        }
        return Observable.zip(...obs).pipe(
          map((res) => {
            this.updateInspection(inspection);
            return res;
          })
        );
      })
    );
  }

  public updatePointStatus(id: string, pointId: string, status: number): Observable<any> {
    return this.getInspection(id).pipe(
      map((inspection) => {
        const point = inspection.points.find((p) => p.id === pointId);
        let inspectionProgression = 0;
        if (point) {
          point.status = status;
          point.etag = new Date();
          inspectionProgression = this.calculateProgression(inspection);
        }
        this.updateInspection(inspection);
        return { point, inspectionProgression };
      })
    );
  }

  public updatePointsStatus(id: string, pointIds: string[], status: number): Observable<any> {
    return this.getInspection(id).pipe(
      map((inspection) => {
        const points = [];
        pointIds.forEach((pointId) => {
          const point = inspection.points.find((p) => p.id === pointId);
          if (point) {
            point.status = status;
            point.etag = new Date();
            points.push(point);
          }
        });
        const inspectionProgression = this.calculateProgression(inspection);
        this.updateInspection(inspection);
        return { points, inspectionProgression };
      })
    );
  }

  public updatePointNotes(id: string, pointId: string, data: any): Observable<any> {
    return this.getInspection(id).pipe(
      map((inspection) => {
        const point = inspection.points.find((p) => p.id === pointId);
        if (point) {
          point.notes = data.notes;
          point.etag = new Date();
        }
        this.updateInspection(inspection);
        return point;
      })
    );
  }

  public setNotApplicable(id: string, pointId: string, data: any): Observable<any> {
    return this.getInspection(id).pipe(
      map((inspection) => {
        const point = inspection.points.find((p) => p.id === pointId);
        let inspectionProgression = 0;
        if (point) {
          Object.assign(point, data);
          point.status = data.reason || data.reason === 0 ? 200 : 0;
          point.etag = new Date();
          inspectionProgression = this.calculateProgression(inspection);
        }
        this.updateInspection(inspection);
        return { point, inspectionProgression };
      })
    );
  }

  public orderPlugins(id: string, pointId: string, orderableItems: any[]) {
    return this.getInspection(id).pipe(
      map((inspection) => {
        const plugins = inspection.allPlugins.filter((p) => p.pointId === pointId);
        orderableItems.forEach((item) => {
          const plugin = plugins.find((p) => p.id === item.id);
          if (plugin) {
            plugin.etag = new Date();
            plugin.order = item.order;
          }
        });
        this.updateInspection(inspection);
        return plugins;
      })
    );
  }

  public updatePointProperties(id: string, pointId: string, data: any): Observable<any> {
    return this.getInspection(id).pipe(
      map((inspection) => {
        const point = inspection.points.find((p) => p.id === pointId);
        if (point) {
          point.etag = new Date();
          Object.assign(point, data);
          this.updateTranslations(inspection, pointId, data);
        }
        this.updateInspection(inspection);
        return point;
      })
    );
  }

  public createPoint(inspectionId: string, sectionId: string, folderId: string, model: any): Observable<any> {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const section = inspection.sections.find((s) => s.id === sectionId);
        if (section) {
          const point = new InspectionPoint(this.guid(), model.name, model.description);
          point.sectionId = sectionId;
          point.inspectionId = inspectionId;
          point.etag = new Date();
          point.status = 0;
          if (folderId) {
            point.folderId = folderId;
            const points = inspection.points.filter((p) => p.sectionId === sectionId && p.folderId === folderId);
            point.order = this.findMaxOrder(points);
          } else {
            const points = inspection.points.filter((p) => p.sectionId === sectionId);
            point.order = this.findMaxOrder([...points, ...(section.folders ? section.folders : [])]);
          }
          inspection.points.push(point);
          this.updateInspection(inspection);
          return point;
        }
      })
    );
  }

  public deletePoints(id: string, pointIds: Array<string>): Observable<any> {
    return this.getInspection(id).pipe(
      map((inspection) => {
        if (inspection.applicationType === ApplicationType.Maintenance) {
          const partsPlugin = inspection.allPlugins.find((p) => p.pluginType === 'parts');
          if (partsPlugin) {
            pointIds.forEach((pointId) => this._deleteParts(partsPlugin, pointId));
          }
        }
        const points = inspection.points.filter((p) => pointIds.includes(p.id));
        inspection.points = inspection.points.filter((p) => !pointIds.includes(p.id));
        inspection.allPlugins = inspection.allPlugins.filter((p) => !pointIds.includes(p.pointId));
        const inspectionProgression = this.calculateProgression(inspection);
        if (inspection.deletedPoints) {
          inspection.deletedPoints.push(...pointIds);
        } else {
          inspection.deletedPoints = [...pointIds];
        }
        this.updateInspection(inspection);
        return { deletedPoints: points.map((p) => p.id), inspectionProgression };
      })
    );
  }

  public deleteFolders(id: string, sectionId: string, folderIds: string[]): Observable<any> {
    return this.getInspection(id).pipe(
      map((inspection) => {
        const section = inspection.sections.find((s) => s.id === sectionId);
        if (section) {
          if (section.folders) {
            section.folders = section.folders.filter((f) => !folderIds.includes(f.id));
          }
          const pointsByFolder = inspection.points.filter((p) => p.folderId && folderIds.includes(p.folderId)).map((p) => p.id);
          inspection.points = inspection.points.filter((p) => !pointsByFolder.includes(p.id));
          inspection.allPlugins = inspection.allPlugins.filter((p) => !pointsByFolder.includes(p.pointId));
          if (inspection.applicationType === ApplicationType.Maintenance) {
            const partsPlugin = inspection.allPlugins.find((p) => p.pluginType === 'parts');
            if (partsPlugin) {
              pointsByFolder.forEach((pointId) => this._deleteParts(partsPlugin, pointId));
            }
          }
          if (inspection.deletedFolders) {
            inspection.deletedFolders.push(...folderIds);
          } else {
            inspection.deletedFolders = [...folderIds];
          }
          const inspectionProgression = this.calculateProgression(inspection);
          this.updateInspection(inspection);
          return { deletedPoints: pointsByFolder, deletedFolders: folderIds, inspectionProgression };
        }
        return null;
      })
    );
  }

  public updatePluginConfig(inspectionId: string, pointId: string, pluginId: string, data: any, propertyName?: string) {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        map((plugin) => {
          if (plugin) {
            if (propertyName) {
              plugin.config[propertyName] = data;
            } else {
              plugin.config = data;
            }
            return plugin;
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const plugin = inspection.allPlugins.find((p) => p.id === pluginId);
        if (plugin) {
          plugin.etag = new Date();
          if (propertyName) {
            plugin.config[propertyName] = data;
          } else {
            plugin.config = data;
          }
          this.updateInspection(inspection);
          return plugin;
        }
        return null;
      })
    );
  }

  public deletePlugin(inspectionId: string, pointId: string, pluginId: string) {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        inspection.allPlugins = inspection.allPlugins.filter((plugin) => plugin.id !== pluginId);
        inspection.plugins = inspection.plugins.filter((plugin) => plugin.id !== pluginId);
        if (inspection.deletedPlugins) {
          inspection.deletedPlugins.push(pluginId);
        } else {
          inspection.deletedPlugins = [pluginId];
        }
        this.updateInspection(inspection);
        return true;
      })
    );
  }

  public createFolder(inspectionId: string, sectionId: string, model: any) {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const section = inspection.sections.find((s) => s.id === sectionId);
        const points = inspection.points.filter((p) => p.sectionId === sectionId);

        const folder = new InspectionFolder(this.guid(), model.name, '');
        folder.etag = new Date();
        if (!section.folders) {
          section.folders = [];
        }
        folder.order = this.findMaxOrder([...section.folders, ...points]);
        section.folders.push(folder);
        this.updateInspection(inspection);
        return folder;
      })
    );
  }

  public updateFolder(inspectionId: string, sectionId: string, folderId: string, model: any) {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const section = inspection.sections.find((s) => s.id === sectionId);
        if (section) {
          const folder = section.folders.find((f) => f.id === folderId);
          if (folder) {
            folder.etag = new Date();
            Object.assign(folder, model);
            this.updateTranslations(inspection, folderId, model);
          }
        }
        this.updateInspection(inspection);
        return section;
      })
    );
  }

  public updateSection(inspectionId: string, sectionId: string, model: any) {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const section = inspection.sections.find((s) => s.id === sectionId);
        if (section) {
          section.etag = new Date();
          Object.assign(section, model);
          this.updateTranslations(inspection, sectionId, model);
        }
        this.updateInspection(inspection);
        return section;
      })
    );
  }

  public orderPoints(inspectionId: string, sectionId: string, orderableItems: any[]) {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const section = inspection.sections.find((s) => s.id === sectionId);
        if (section) {
          const points = inspection.points.filter((p) => p.sectionId === sectionId);
          orderableItems.forEach((item) => {
            if (item.type === 200) {
              const point = points.find((p) => p.id === item.id);
              if (point) {
                point.etag = new Date();
                point.order = item.order;
              }
            } else if (item.type === 100 && section.folders) {
              const folder = section.folders.find((f) => f.id === item.id);
              if (folder) {
                folder.etag = new Date();
                folder.order = item.order;
              }
            }
          });
          this.updateInspection(inspection);
          return section;
        }
        return null;
      })
    );
  }

  public deleteSections(inspectionId: string, sectionIds: Array<string>): Observable<any> {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const sections = inspection.sections.filter((s) => sectionIds.includes(s.id));
        if (sections.length > 0) {
          const pointIds = inspection.points.filter((p) => sectionIds.includes(p.sectionId)).map((p) => p.id);
          inspection.sections = inspection.sections.filter((s) => !sectionIds.includes(s.id));
          inspection.points = inspection.points.filter((p) => !pointIds.includes(p.id));
          inspection.allPlugins = inspection.allPlugins.filter((p) => !pointIds.includes(p.pointId));

          if (inspection.applicationType === ApplicationType.Maintenance) {
            const partsPlugin = inspection.allPlugins.find((p) => p.pluginType === 'parts');
            if (partsPlugin) {
              pointIds.forEach((pointId) => this._deleteParts(partsPlugin, pointId));
            }
          }

          const inspectionProgression = this.calculateProgression(inspection);
          if (inspection.deletedSections) {
            inspection.deletedSections.push(...sectionIds);
          } else {
            inspection.deletedSections = [...sectionIds];
          }
          this.updateInspection(inspection);
          return { sectionsDelete: true, inspectionProgression };
        }
        return null;
      })
    );
  }

  public deleteSection(inspectionId: string, sectionId: string): Observable<any> {
    return this.deleteSections(inspectionId, [sectionId]);
  }

  public createSection(inspectionId: string, model: any): Observable<any> {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const section = new InspectionSection(this.guid(), model.name, model.description);
        section.etag = new Date();
        section.inspectionId = inspectionId;
        section.order = this.findMaxOrder(inspection.sectionsItems);
        inspection.sections.push(section);
        inspection.sectionsItems.push({ id: section.id, order: section.order });
        this.updateInspection(inspection);
        return section;
      })
    );
  }

  public updateInspectionProperties(inspectionId: string, model: any): Observable<any> {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        model.status = model.status ? +model.status : null;
        inspection.etag = new Date();
        Object.assign(inspection, model);
        this.updateTranslations(inspection, inspectionId, model);
        this.updateInspection(inspection);
        return inspection;
      })
    );
  }

  public orderSections(inspectionId: string, orderableItems: any[]): Observable<any> {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        orderableItems.forEach((item) => {
          const section = inspection.sections.find((s) => s.id === item.id);
          if (section) {
            section.etag = new Date();
            section.order = item.order;
          }
          const sectionItem = inspection.sectionsItems.find((s) => s.id === item.id);
          if (sectionItem) {
            sectionItem.order = item.order;
          }
        });
        this.updateInspection(inspection);
        return inspection;
      })
    );
  }

  public orderInspectionPlugins(inspectionId: string, orderableItems: any[]): Observable<any> {
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        orderableItems.forEach((item) => {
          const plugin = inspection.allPlugins.find((p) => p.id === item.id);
          if (plugin) {
            plugin.etag = new Date();
            plugin.order = item.order;
          }
          const inspectionPlugin = inspection.plugins.find((p) => p.id === item.id);
          if (inspectionPlugin) {
            inspectionPlugin.etag = new Date();
            inspectionPlugin.order = item.order;
          }
        });
        this.updateInspection(inspection);
        return inspection;
      })
    );
  }

  public importPoints(data: any): Observable<any> {
    return this.getInspection(data.id).pipe(
      switchMap((inspection) => {
        const section = inspection.sections.find((s) => s.id === data.targetSectionId);
        if (section) {
          return this.indexedDb.read(AppConstants.INDEXEDDB_TABLES.templates, data.sourceInspectionId).pipe(
            map((template: Inspection) => {
              data.sourcePointsId.forEach((pointId) => {
                const point = template.points.find((p) => p.id === pointId);
                if (point) {
                  const points = inspection.points.filter((p) => p.sectionId === section.id);
                  const folders = section.folders ? section.folders : [];
                  const order = this.findMaxOrder([...points, ...folders]);
                  const res = this._copyPoint(point, inspection.id, section.id, data.targetFolderId ? data.targetFolderId : null, order);
                  inspection.points.push(res);
                  section.etag = new Date();

                  const plugins = template.allPlugins.filter((p) => p.pointId === point.id);
                  plugins.forEach((plugin) => {
                    const pluginRes = this._copyPlugin(plugin, inspection, res);
                  });
                }
              });
              this.updateInspection(inspection);
              return inspection;
            })
          );
        }
        return null;
      })
    );
  }

  public importSections(data: any): Observable<any> {
    const source$ = this.indexedDb.read(AppConstants.INDEXEDDB_TABLES.templates, data.sourceInspectionId);
    return this.copySections(data.id, source$, data.sourceSectionsId, false);
  }

  public copySections(id, source: Observable<Inspection>, sourceSectionsId, markAsCopy: boolean): Observable<Inspection> {
    const obs = [];
    return zip(this.getInspection(id), source).pipe(
      map(([inspection, sourceInspection]) => {
        sourceSectionsId.forEach((sectionId) => {
          const section = sourceInspection.sections.find((s) => s.id === sectionId);
          const res = new InspectionSection(this.guid(), section.name + (markAsCopy ? ' copy' : ''), section.description);
          res.etag = new Date();
          res.inspectionId = inspection.id;
          res.order = this.findMaxOrder(inspection.sectionsItems);
          const foldersMapping = {};
          if (section.folders && section.folders.length > 0) {
            res.folders = [];
            section.folders.forEach((f) => {
              const folder = new InspectionFolder(this.guid(), f.name, f.description);
              folder.order = f.order;
              folder.etag = new Date();
              res.folders.push(folder);
              foldersMapping[f.id] = folder.id;
            });
          }
          inspection.etag = new Date();
          inspection.sections.push(res);
          inspection.sectionsItems.push({ id: res.id, order: res.order });

          const points = sourceInspection.points.filter((p) => p.sectionId === sectionId);

          points.forEach((p) => {
            const sectionPoints = inspection.points.filter((p) => p.sectionId === sectionId);
            const order = this.findMaxOrder([...(sectionPoints ?? []), ...(res.folders ?? [])]);
            const point = this._copyPoint(
              p,
              inspection.id,
              res.id,
              p.folderId ? (foldersMapping[p.folderId] ? foldersMapping[p.folderId] : null) : null,
              order
            );
            inspection.points.push(point);
            const plugins = sourceInspection.allPlugins.filter((plugin) => plugin.pointId === p.id);
            plugins.forEach((plugin) => {
              if (plugin.pluginType === 'pointparts') {
                obs.push(this._addPlugin(inspection, point.id, 'pointparts'));
                //this._copyParts(sourceInspection, inspection, p.id, point.id);
              } else {
                const pluginRes = this._copyPlugin(plugin, inspection, point);
              }
            });
          });
        });
        //this.updateInspection(inspection);
        return inspection;
      }),
      switchMap((inspection) => {
        if (obs.length > 0) {
          const partsPlugins = inspection.allPlugins.find((p) => p.pluginType === 'parts');
          if (!partsPlugins) {
            obs.push(this._addPlugin(inspection, null, 'parts'));
          }
        }
        return Observable.zip(...obs, of(null)).pipe(
          map((res) => {
            this.updateInspection(inspection);
            return inspection;
          })
        );
      })
    );
  }

  public copyToSection(
    inspectionId: string,
    sectionId: string,
    folderId: string,
    source$: Observable<Inspection>,
    data: any
  ): Observable<any> {
    const obs = [];
    return zip(this.getInspection(inspectionId), source$).pipe(
      map(([inspection, sourceInspection]) => {
        const section = inspection.sections.find((s) => s.id === sectionId);
        const sourceSection = sourceInspection.sections.find((s) => s.id === data.sourceSectionId);
        if (section && sourceSection) {
          data.sectionItems.forEach((item) => {
            const points = inspection.points.filter((p) => p.sectionId === section.id);
            let order = this.findMaxOrder([...points, ...(section.folders ? section.folders : [])]);
            if (item.isFolder) {
              if (folderId) {
                return;
              }
              if (!section.folders) {
                section.folders = [];
              }
              const sourceFolder = sourceSection.folders.find((f) => f.id === item.id);
              if (sourceFolder) {
                const sectionPoints = inspection.points.filter((p) => p.sectionId === section.id);
                const itemOrder = this.findMaxOrder([...sectionPoints, ...section.folders]);

                const folder = new InspectionFolder(this.guid(), sourceFolder.name, sourceFolder.description);
                folder.order = itemOrder;
                folder.etag = new Date();
                section.folders.push(folder);

                const pointsByFolder = sourceInspection.points.filter(
                  (p) => p.sectionId === sourceSection.id && p.folderId === sourceFolder.id
                );

                pointsByFolder.forEach((p) => {
                  const folderPoints = inspection.points.filter((p) => p.folderId === folderId);
                  const pointOrder = this.findMaxOrder([...folderPoints]);
                  const point = this._copyPoint(p, inspection.id, sectionId, folder.id, pointOrder);
                  inspection.points.push(point);

                  const plugins = sourceInspection.allPlugins.filter((plugin) => plugin.pointId === p.id);
                  plugins.forEach((plugin) => {
                    if (plugin.pluginType === 'pointparts') {
                      obs.push(this._addPlugin(inspection, point.id, 'pointparts'));
                      //this._copyParts(sourceInspection, inspection, p.id, point.id);
                    } else {
                      const pluginRes = this._copyPlugin(plugin, inspection, point);
                    }
                  });
                });
              }
            } else {
              const p = sourceInspection.points.find((p) => p.id === item.id);
              if (p) {
                let pointOrder = 0;
                if (folderId) {
                  const folderPoints = inspection.points.filter((p) => p.sectionId === sectionId && p.folderId === folderId);
                  pointOrder = this.findMaxOrder(folderPoints);
                } else {
                  const sectionPoints = inspection.points.filter((p) => p.sectionId === sectionId);
                  pointOrder = this.findMaxOrder([...sectionPoints, ...(section.folders ? section.folders : [])]);
                }
                const point = this._copyPoint(p, inspection.id, sectionId, folderId, pointOrder);
                inspection.points.push(point);
                const plugins = sourceInspection.allPlugins.filter((plugin) => plugin.pointId === p.id);
                plugins.forEach((plugin) => {
                  if (plugin.pluginType === 'pointparts') {
                    obs.push(this._addPlugin(inspection, point.id, 'pointparts'));
                  } else {
                    const pluginRes = this._copyPlugin(plugin, inspection, point);
                  }
                });
              }
            }
          });
          section.etag = new Date();
          return inspection;
        }
      }),
      switchMap((inspection) => {
        if (obs.length > 0) {
          const partsPlugins = inspection.allPlugins.find((p) => p.pluginType === 'parts');
          if (!partsPlugins) {
            obs.push(this._addPlugin(inspection, null, 'parts'));
          }
        }
        return Observable.zip(...obs, of(null)).pipe(
          map((res) => {
            this.updateInspection(inspection);
            return inspection;
          })
        );
      })
    );
  }

  public continueLocal(inspectionId: string): Observable<any> {
    return this.getInspection(inspectionId).pipe(
      switchMap((inspection) => {
        inspection.updatedOffline = false;
        this.dataService.updateInspectionOffline(inspection.id, false);
        return this.indexedDb.put(AppConstants.INDEXEDDB_TABLES.inspections, JSON.parse(JSON.stringify(inspection)), inspection.id);
      })
    );
  }

  protected findMaxOrder(list: any[]) {
    if (list.length > 0) {
      return Math.max(...list.map((l) => l.order)) + 1;
    } else {
      return 0;
    }
  }

  private calculateProgression(inspection: Inspection): number {
    const pointDoneCount = inspection.points.filter((p) => p.status === 100 || p.status === 200).length;
    const inspectionProgression =
      pointDoneCount === 0 || inspection.points.length === 0 ? 0 : (pointDoneCount / inspection.points.length) * 100;

    inspection.progression = Math.round(inspectionProgression);

    return Math.round(inspectionProgression);
  }

  protected getInspection(inspectionId: string): Observable<Inspection> {
    return this.indexedDb.read<Inspection>(AppConstants.INDEXEDDB_TABLES.inspections, inspectionId);
  }

  protected updateInspection(inspection: Inspection, updateOffline = true) {
    if (inspection.isOffline && updateOffline) {
      inspection.updatedOffline = true;
      inspection.isSent = false;
      this.dataService.updateInspectionOffline(inspection.id, true);
    }
    this.indexedDb.put(AppConstants.INDEXEDDB_TABLES.inspections, JSON.parse(JSON.stringify(inspection)), inspection.id).subscribe();
  }

  private _addPlugin(inspection: Inspection, pointId: string, pluginType: string): Observable<InspectionPlugin> {
    return this.indexedDb.read(AppConstants.INDEXEDDB_TABLES.pluginModels, pluginType).pipe(
      map((pluginModel) => {
        const plugin: InspectionPlugin = new InspectionPlugin(this.guid(), null, pluginType);
        plugin.inspectionId = inspection.id;
        plugin.pointId = pointId ? pointId : null;
        plugin.config = pluginModel.Configuration;
        plugin.pluginType = pluginModel.PluginType;
        plugin.required = pluginModel.Required;
        plugin.etag = new Date();
        plugin.propertyList = [];
        plugin.references = pluginModel.References;
        plugin.description = pluginModel.Description;
        plugin.name = pluginModel.Name;

        if (pointId) {
          const plugins = inspection.allPlugins.filter((p) => p.pointId === pointId);
          plugin.order = this.findMaxOrder(plugins);
        } else {
          plugin.order = this.findMaxOrder(inspection.plugins);
        }
        inspection.allPlugins.push(plugin);
        return plugin;
      })
    );
  }

  private _deleteParts(partsPlugin: any, pointId: string) {
    const config = partsPlugin.config;
    config.Parts = config.Parts.filter((p) => p.PointId !== pointId);
    if (config.PointParts[pointId]) {
      config.PointParts[pointId] = {};
    }
    if (config.PointParts['-1']) {
      config.PointParts['-1'] = null;
    }
  }

  private _copyParts(sourceInspection: Inspection, targetInspection: Inspection, sourcePointId: string, targetPointId: string) {
    const sourcePartsPlugin = sourceInspection.allPlugins.find((p) => p.pluginType === 'parts');
    if (sourcePartsPlugin) {
      const partsPlugins = targetInspection.allPlugins.find((p) => p.pluginType === 'parts');
      /*if (!partsPlugins) {
        partsPlugins = this._addPlugin(inspection, null, 'parts');
      }*/
      const sourcePartsConfig = sourcePartsPlugin.config;
      const partsConfig = partsPlugins.config;

      sourcePartsConfig.Parts = sourcePartsConfig.Parts ?? [];
      const partsToAdd = sourcePartsConfig.Parts.filter((x) => x.PointId === sourcePointId).map((x) => {
        return Object.assign({}, x, { PointId: targetPointId });
      });
      partsConfig.Parts.push(...partsToAdd);
      partsConfig.Basket.PartsView.push(
        ...partsToAdd
          .filter((x) => !partsConfig.Basket.PartsView.map((pa) => pa.Parts).includes(x.Id))
          .map((x) => ({
            Parts: x.Id,
            Quantity: x.Quantity,
            Machine: x.EquipmentId,
            Page: x.Page,
            Position: x.Position,
            Name: x.Name
          }))
      );
      if (sourcePartsConfig.PointParts[sourcePointId]) {
        const sourcePointModel = sourcePartsConfig.PointParts[sourcePointId];
        partsConfig.PointParts[targetPointId] = {
          PointId: targetPointId,
          HybrisToken: sourcePointModel.HybrisToken,
          HybrisUrl: sourcePointModel.HybrisUrl,
          HybrisTokenExpiration: sourcePointModel.HybrisTokenExpiration,
          EquipmentId: sourcePointModel.EquipmentId,
          Key: sourcePointModel.Key
        };
      }
    }
  }

  public guid() {
    return uuid();
  }

  private _copyPlugin(sourcePlugin: InspectionPlugin, targetInspection: Inspection, targetPoint: InspectionPoint) {
    const targetInspectionId: string = targetInspection?.id;
    const targetPointId: string = targetPoint?.id;
    const pluginRes = new InspectionPlugin(this.guid(), sourcePlugin.order, sourcePlugin.pluginType);
    Object.assign(pluginRes, _.cloneDeep(_.omit(sourcePlugin, ['id'])));
    pluginRes.etag = new Date();
    pluginRes.inspectionId = targetInspectionId;
    pluginRes.pointId = targetPointId;

    if (AppConstants.PLUGINS_URL[pluginRes.pluginType]) {
      const url = AppConstants.PLUGINS_URL[pluginRes.pluginType](pluginRes);
      pluginRes.postServerUrl = url;
    }

    if (targetInspection && targetInspection.allPlugins) {
      targetInspection.allPlugins.push(pluginRes);
    }

    if (targetPoint && targetPoint.plugins) {
      targetPoint.plugins.push(pluginRes);
    }

    return pluginRes;
  }

  private _copyPoint(
    point: InspectionPoint,
    inspectionId: string,
    sectionId: string,
    folderId: string = null,
    order: number = 0,
    status: number = null
  ) {
    const newPoint = new InspectionPoint(this.guid(), point.name, point.description);
    newPoint.etag = new Date();
    newPoint.inspectionId = inspectionId;
    newPoint.sectionId = sectionId;
    newPoint.folderId = folderId;
    newPoint.order = order;
    newPoint.status = status == null ? point.status : status;
    return newPoint;
  }

  private updateTranslations(inspection: any, objectId: string, model: any) {
    if (!inspection || !inspection.translations || !model) {
      return;
    }
    _.forOwn(model, (value, key: string) => {
      if (!value || typeof value !== 'string') {
        return;
      }
      const translationKey = `${objectId}.${key}`;
      const translations = inspection.translations[translationKey];
      if (translations && translations[inspection.inputLanguage]) {
        translations[inspection.inputLanguage] = value;
      }
    });
  }
}
